import React from 'react';

import { Alert, Flex } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import UnitInformationCard from '@modules/roster-lite/components/RosterLitePage/UnitInformationCard';
import { canViewUnitRosterSel } from '@modules/roster-lite/duck/selectors';
import { useUnitRoster } from '@modules/roster-lite/duck/unitRosterHooks';
import PageContainer from '@shared/components/Layouts/PageContainer';
import Spin from '@shared/components/Spin';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import UnitRosterTable from './UnitRosterTable';

interface UnitRosterRouteParams {
  orgGuid?: string;
}

interface UnitRosterContainerProps {
  children: React.ReactNode;
}

export function UnitRosterContainer(props: UnitRosterContainerProps) {
  const { children } = props;

  const { ready, allowed } = useSelector(canViewUnitRosterSel);
  const enabled = featureFlags.getFlag('SBL_6895_UNIT_ROSTER');

  if (!ready) {
    return (
      <PageContainer>
        <Spin />
      </PageContainer>
    );
  }

  if (!enabled || !allowed) return null;

  return <>{children}</>;
}

const UnitRosterPageContents: React.FC = () => {
  const { orgGuid: routeOrgGuid } = useSelector(
    routeParamsSel<UnitRosterRouteParams>,
  );
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const guid = routeOrgGuid ?? selectedOrgGuid;

  const { members, isFetching, isError, errorMessage } = useUnitRoster(guid);

  const hasError = isError && !isFetching;

  return (
    <PageContainer>
      <Flex vertical gap="middle">
        {hasError ? (
          <Alert
            type="error"
            showIcon
            message={intl.formatMessage({ id: 'unitRoster.error.title' })}
            description={
              errorMessage ?? (
                <FormattedMessage id="unitRoster.error.description" />
              )
            }
          />
        ) : (
          <>
            <UnitInformationCard organizationGuid={guid} />
            <UnitRosterTable
              guid={guid}
              members={members}
              loading={isFetching}
            />
          </>
        )}
      </Flex>
    </PageContainer>
  );
};

export default function UnitRosterPage() {
  return (
    <UnitRosterContainer>
      <UnitRosterPageContents />
    </UnitRosterContainer>
  );
}
